import { useEffect, useRef, useState } from 'react';

interface Service {
  id: string;
  no: string;
  title: string;
  text: string;
  capabilities: string[];
  img: string;
}

interface Props {
  service: Service;
  total: number;
}

export default function ServiceScreen({ service: s, total }: Props) {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setInView(true);
    }, { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const short = s.text.split('. ')[0].replace(/\.$/, '');

  return (
    <section
      ref={ref}
      id={s.id}
      aria-label={`${s.no} of ${total} — ${s.title}`}
      className={`scroll-mt-14 relative w-full min-h-[calc(100dvh-56px)] flex overflow-hidden bg-foreground-950 text-white ${inView ? 'svc-in' : ''}`}
    >
      <img
        src={s.img}
        alt={`${s.title} — blue-toned architectural photograph`}
        title={`AUREN ${s.title}`}
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/75 via-secondary-950/35 to-secondary-950/10" />
      <div className="relative wrap w-full flex flex-col justify-between py-12 md:py-16">
        <div className="flex items-center justify-between label-xs text-white/70">
          <span>Expertise / AUREN</span>
          <span className="tabular-nums">{s.no} / {String(total).padStart(2, '0')}</span>
        </div>
        <div className="svc-copy max-w-[620px] py-16">
          <h2 className="font-heading font-light uppercase leading-[0.95] tracking-[0.02em] text-[clamp(38px,6vw,88px)]">{s.title}</h2>
          <p className="mt-8 text-[15px] md:text-[16px] leading-[1.7] text-white/80 max-w-[420px]">{short}.</p>
        </div>
        <ul className="svc-copy flex flex-wrap gap-x-8 gap-y-3 border-t border-white/30 pt-5 label-xs text-white/85">
          {s.capabilities.map((c) => <li key={c} className="whitespace-nowrap">{c}</li>)}
        </ul>
      </div>
      <div className="exp-dim absolute inset-0 z-10 bg-foreground-950 opacity-0 pointer-events-none" aria-hidden="true" />
    </section>
  );
}